import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Heart } from 'lucide-react'
import { useVersion } from '../contexts/VersionContext'

// Define all possible navigation items with version visibility
const allNavigationItems = [
  { name: 'Home', href: '/', showInV1: true, showInV2: true },
  { name: 'Itinerary', href: '/itinerary', showInV1: false, showInV2: true },
  { name: 'Accommodations + Travel', href: '/accommodations', showInV1: true, showInV2: true },
  { name: 'Wedding Party', href: '/wedding-party', showInV1: false, showInV2: false },
  { name: 'Photos', href: '/photos', showInV1: true, showInV2: true },
  { name: 'Attire', href: '/attire', showInV1: true, showInV2: true },
  { name: 'Q + A', href: '/qa', showInV1: true, showInV2: true },
  { name: 'Registry', href: '/registry', showInV1: true, showInV2: true },
  { name: 'RSVP', href: '/rsvp', showInV1: false, showInV2: true },
]

export function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const { isV1, isV2 } = useVersion()

  // Filter navigation items based on current version
  const navigation = allNavigationItems.filter(item => {
    if (isV1) return item.showInV1
    if (isV2) return item.showInV2
    return true // default version shows all items
  })

  const isActive = (href: string) => location.pathname === href

  return (
    <nav className="bg-white shadow-sm border-b border-neutral-200 fixed w-full top-0 z-50">
      <div className="container-max">
        <div className="flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <Heart className="h-8 w-8 text-primary-600" />
            <div className="text-left">
              <div className="script-font text-xl text-primary-700">Naetzy & Eduardo</div>
              <div className="text-xs text-earth-700">April 4, 2026</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigation.map((item) => {
              const isRSVP = item.name === 'RSVP'
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-all duration-200 ${
                    isRSVP
                      ? `px-4 py-2 rounded-md border-2 ${
                          isActive(item.href)
                            ? 'bg-primary-600 text-white border-primary-600 shadow-lg'
                            : 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700 hover:border-primary-700 shadow-md hover:shadow-lg'
                        }`
                      : isActive(item.href)
                      ? 'text-primary-700 border-b-2 border-primary-600 pb-1'
                      : 'text-earth-700 hover:text-primary-700'
                  }`}
                >
                  {item.name}
                </Link>
              )
            })}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-neutral-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md p-2"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-neutral-200 bg-white">
            <div className="px-4 py-2 space-y-1">
              {navigation.map((item) => {
                const isRSVP = item.name === 'RSVP'
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium transition-all duration-200 ${
                      isRSVP
                        ? `text-center border-2 rounded-md ${
                            isActive(item.href)
                              ? 'bg-primary-600 text-white border-primary-600 shadow-lg'
                              : 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700 hover:border-primary-700 shadow-md'
                          }`
                        : `rounded-md ${
                            isActive(item.href)
                              ? 'text-primary-700 bg-primary-50'
                              : 'text-neutral-600 hover:text-primary-700 hover:bg-neutral-50'
                          }`
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
} 